/**
 * Phase 135 — StreamX Public Wall (Cinematic Live Network Showcase)
 * --------------------------------------------------------------------
 * Network-effect amplification surface. Rendered on the /livex landing page
 * just below the hero. Shows visible ecosystem vitality in 3 layers:
 *
 *   1. **Pulse strip** — animated counters (live streams, live academies,
 *      total viewers). Refreshes every 60s.
 *   2. **Mosaic** — auto-fetched live streams from /livex/streams/live as
 *      premium broadcast cards with shimmer hover + animated LIVE pulse +
 *      gradient overlays. Horizontal snap-scroll on mobile.
 *   3. **Trending strip** — Most Watched / Fastest Growing / Top Academy
 *      Today narrative chips.
 *
 * Performance: lazy fetch on mount + lightweight 60s polling. No autoplay
 * video, no heavy GPU effects. CSS-only motion (matches livex.css tokens).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Eye, Flame, Zap, Trophy, ArrowRight, Sparkles, Users } from 'lucide-react';
import '@/styles/livex.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatCount(n) {
  const v = Number(n || 0);
  if (v >= 1000) return `${(v / 1000).toFixed(1)}K`;
  return v.toLocaleString('en-IN');
}

function TileShimmer() {
  return (
    <div className="bg-[#07111F] border-2 border-[#22314F] rounded-2xl overflow-hidden flex-shrink-0 w-[280px] sm:w-auto">
      <div className="aspect-video bg-[#0c1a2e] livex-shimmer" />
      <div className="p-3 space-y-2">
        <div className="h-4 w-3/4 livex-shimmer rounded" />
        <div className="h-3 w-1/2 livex-shimmer rounded" />
      </div>
    </div>
  );
}

function StreamTile({ s }) {
  const accentBySport = {
    football: '#10b981',
    cricket: '#f59e0b',
    basketball: '#f97316',
    tennis: '#a855f7',
    hockey: '#3b82f6',
    badminton: '#06b6d4',
  };
  const accent = accentBySport[(s.sport || '').toLowerCase()] || '#FF5A1F';
  return (
    <Link
      to={`/livex/watch/${s.id}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`livex-wall-tile-${s.id}`}
      className="group relative block bg-[#07111F] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-2xl overflow-hidden livex-card-lift flex-shrink-0 w-[280px] sm:w-auto"
    >
      {/* Thumbnail / cinematic placeholder */}
      <div
        className="aspect-video relative overflow-hidden"
        style={{
          background: `radial-gradient(circle at 30% 40%, ${accent}33, #050a14 70%), linear-gradient(135deg, #0c1a2e, #050a14)`,
        }}
      >
        {/* Subtle moving sheen on hover */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background: `radial-gradient(circle at 70% 60%, ${accent}25, transparent 50%)`,
          }}
        />
        {/* Center icon medallion */}
        <div className="absolute inset-0 grid place-items-center">
          <div
            className="h-14 w-14 rounded-2xl grid place-items-center transition-transform group-hover:scale-110"
            style={{
              background: `linear-gradient(135deg, ${accent}, ${accent}88)`,
              boxShadow: `0 12px 32px ${accent}55`,
            }}
          >
            <Radio className="h-6 w-6 text-white" />
          </div>
        </div>
        {/* LIVE badge */}
        <div className="absolute top-3 left-3">
          <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-rose-600 text-white text-[10px] font-black uppercase tracking-wider rounded">
            <span className="h-1.5 w-1.5 rounded-full bg-white livex-live-dot" /> LIVE
          </span>
        </div>
        {/* Viewer count chip */}
        <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 bg-black/70 backdrop-blur text-white text-[10px] font-bold rounded">
          <Eye className="h-2.5 w-2.5" /> {formatCount(s.viewer_count)}
        </div>
        {/* Sport chip */}
        {s.sport && (
          <div
            className="absolute bottom-3 left-3 inline-flex items-center px-2 py-1 text-[10px] font-black uppercase tracking-wider rounded text-white"
            style={{ background: `${accent}cc`, backdropFilter: 'blur(4px)' }}
          >
            {s.sport}
          </div>
        )}
        {/* Bottom gradient for legibility */}
        <div
          aria-hidden
          className="absolute inset-x-0 bottom-0 h-16 pointer-events-none"
          style={{ background: 'linear-gradient(to top, rgba(5,10,20,0.85), transparent)' }}
        />
      </div>
      <div className="p-3">
        <h3 className="text-sm font-black text-white truncate">{s.title}</h3>
        <div className="text-[11px] font-bold text-[#94A3B8] truncate mt-0.5">
          {s.academy_name || 'StreamX Academy'}
        </div>
      </div>
    </Link>
  );
}

function TrendingCard({ icon: Icon, label, value, sublabel, accent }) {
  return (
    <div
      className="p-4 sm:p-5 bg-[#07111F] border-2 border-[#22314F] rounded-2xl flex items-start gap-3 livex-card-lift"
    >
      <div
        className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl"
        style={{
          background: `${accent}22`,
          border: `2px solid ${accent}55`,
        }}
      >
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-black uppercase tracking-[0.15em]" style={{ color: accent }}>{label}</div>
        <div className="text-sm font-black text-white truncate mt-0.5">{value}</div>
        {sublabel && (
          <div className="text-[11px] font-bold text-[#94A3B8] truncate mt-0.5">{sublabel}</div>
        )}
      </div>
    </div>
  );
}

export default function LivexPublicWall() {
  const [pulse, setPulse] = useState(null);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const [pulseRes, streamsRes] = await Promise.all([
        fetch(`${API}/livex/network/pulse`).then((r) => r.ok ? r.json() : null).catch(() => null),
        fetch(`${API}/livex/streams/live?limit=6`).then((r) => r.ok ? r.json() : { entries: [] }).catch(() => ({ entries: [] })),
      ]);
      setPulse(pulseRes || null);
      setStreams(streamsRes?.entries || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    const t = setInterval(reload, 60_000);
    return () => clearInterval(t);
  }, []);

  // Empty network state — keep it inviting, never apologetic.
  if (!loading && (!streams || streams.length === 0)) {
    return (
      <section
        className="py-16 sm:py-20 border-y-2 border-[#22314F]"
        style={{ background: 'linear-gradient(180deg, #050a14, #0a0a0f)' }}
        data-testid="livex-landing-wall"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <div className="text-[11px] font-black tracking-[0.3em] text-rose-400 mb-2 flex items-center gap-2 justify-center">
            <span className="inline-block h-2 w-2 rounded-full bg-rose-500 livex-live-dot" /> STREAMX NETWORK
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-white tracking-tight">The platform is alive 24/7.</h2>
          <p className="livex-body-text mt-3 max-w-xl mx-auto">
            No matches at this exact moment — academies broadcast daily across cricket, football, and more. Drop in any time.
          </p>
          <Link
            to="/streamx/live"
            data-testid="livex-landing-wall-empty-cta"
            className="inline-flex items-center gap-2 mt-6 px-5 py-3 text-sm font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg, #dc2626, #f97316)' }}
          >
            <Radio className="h-4 w-4" /> Open Live Discovery
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section
      className="py-16 sm:py-20 border-y-2 border-[#22314F] relative overflow-hidden"
      style={{ background: 'linear-gradient(180deg, #050a14, #0a0a0f 70%)' }}
      data-testid="livex-landing-wall"
    >
      {/* Decorative network glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(35% 60% at 80% 20%, rgba(255,90,31,0.10), transparent 70%), radial-gradient(40% 70% at 10% 80%, rgba(220,38,38,0.08), transparent 70%)',
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        {/* Pulse counter strip */}
        <div data-testid="livex-landing-wall-pulse" className="text-center mb-8">
          <div className="text-[11px] font-black tracking-[0.3em] text-rose-400 mb-2 flex items-center gap-2 justify-center">
            <span className="inline-block h-2 w-2 rounded-full bg-rose-500 livex-live-dot" />
            LIVE NOW ACROSS STREAMX
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight">
            <span className="text-[#FF5A1F] tabular-nums">{loading ? '—' : formatCount(pulse?.live_streams ?? streams.length)}</span>
            {' '}{(pulse?.live_streams ?? streams.length) === 1 ? 'broadcast' : 'broadcasts'} streaming now
          </h2>
          {pulse && (
            <div className="mt-4 inline-flex items-center gap-4 sm:gap-6 flex-wrap justify-center text-[12px] sm:text-[13px] font-bold text-[#cbd5e1]">
              <span className="inline-flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-[#FF5A1F]" />
                <span className="text-white font-black tabular-nums">{formatCount(pulse.live_academies)}</span> academies live
              </span>
              <span className="text-[#22314F]">·</span>
              <span className="inline-flex items-center gap-1.5">
                <Eye className="h-3.5 w-3.5 text-[#FF5A1F]" />
                <span className="text-white font-black tabular-nums">{formatCount(pulse.live_viewers)}</span> watching right now
              </span>
            </div>
          )}
        </div>

        {/* Mosaic grid — horizontal snap-scroll on mobile, grid on sm+ */}
        <div
          data-testid="livex-landing-wall-mosaic"
          className="flex sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 overflow-x-auto sm:overflow-visible pb-4 sm:pb-0 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'thin' }}
        >
          {loading
            ? Array.from({ length: 3 }).map((_, i) => <div key={i} className="snap-start"><TileShimmer /></div>)
            : streams.map((s) => (
                <div key={s.id} className="snap-start">
                  <StreamTile s={s} />
                </div>
              ))}
        </div>

        {/* Trending strip */}
        {pulse && (pulse.trending?.most_watched || pulse.trending?.fastest_growing || pulse.trending?.top_academy_today) && (
          <div className="mt-8" data-testid="livex-landing-wall-trending">
            <div className="text-[11px] font-black tracking-[0.25em] text-[#FF5A1F] uppercase mb-3 flex items-center gap-2">
              <Flame className="h-3.5 w-3.5" /> Trending right now
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {pulse.trending?.most_watched && (
                <TrendingCard
                  icon={Flame}
                  accent="#dc2626"
                  label="Most Watched Match"
                  value={pulse.trending.most_watched.title}
                  sublabel={`${pulse.trending.most_watched.academy_name} · ${formatCount(pulse.trending.most_watched.viewer_count)} watching`}
                />
              )}
              {pulse.trending?.fastest_growing && (
                <TrendingCard
                  icon={Zap}
                  accent="#f59e0b"
                  label="Fastest Growing Stream"
                  value={pulse.trending.fastest_growing.title}
                  sublabel={`${pulse.trending.fastest_growing.viewers_per_minute}/min · ${pulse.trending.fastest_growing.academy_name}`}
                />
              )}
              {pulse.trending?.top_academy_today && (
                <TrendingCard
                  icon={Trophy}
                  accent="#10b981"
                  label="Top Academy Today"
                  value={pulse.trending.top_academy_today.academy_name}
                  sublabel={`${pulse.trending.top_academy_today.streams_today} stream${pulse.trending.top_academy_today.streams_today === 1 ? '' : 's'} today · ${formatCount(pulse.trending.top_academy_today.viewers_today)} cumulative viewers`}
                />
              )}
            </div>
          </div>
        )}

        {/* CTA row */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/streamx/live"
            data-testid="livex-landing-wall-cta"
            className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold text-white rounded-xl border-2 border-[#FF5A1F] transition-all hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, #dc2626, #f97316)',
              boxShadow: '0 8px 24px rgba(255,90,31,0.30)',
              minHeight: '48px',
            }}
          >
            <Sparkles className="h-4 w-4" /> Open the StreamX Wall
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <Link
            to="/streamx/pricing"
            data-testid="livex-landing-wall-pricing"
            className="inline-flex items-center gap-2 px-6 py-3.5 text-sm font-bold text-white bg-[#0c1a2e] border-2 border-[#22314F] hover:border-[#FF5A1F] rounded-xl transition-all"
            style={{ minHeight: '48px' }}
          >
            Broadcast your academy →
          </Link>
        </div>
      </div>
    </section>
  );
}
